import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, CheckCheck, ExternalLink } from 'lucide-react';

import type { Notification } from '@tasks/contracts';

import { getUnreadCount, listNotifications, markNotificationsRead } from '../../api/notifications';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { humanizeError } from '../../lib/errors';
import { formatRelative } from '../../lib/format';
import { cn } from '../../lib/cn';
import { useToast } from '../../components/Toast';
import { notificationIcon } from './notificationIcons';
import styles from './NotificationPanel.module.css';

const PAGE_SIZE = 20;

/** Колокольчик с счётчиком непрочитанных (UC-ISS-10, docs/06 §2). */
export function NotificationPanel() {
  const [isOpen, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const countQuery = useQuery({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: ({ signal }) => getUnreadCount(signal),
    refetchInterval: 60_000,
    staleTime: 15_000,
  });

  const listQuery = useQuery({
    queryKey: queryKeys.notifications.list(0),
    queryFn: ({ signal }) => listNotifications({ limit: PAGE_SIZE, offset: 0 }, signal),
    enabled: isOpen,
  });

  const readMutation = useMutation({
    mutationFn: markNotificationsRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
    onError: (cause: unknown) => {
      toast.error(humanizeError(cause).message);
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const unread = countQuery.data?.count ?? 0;
  const items = listQuery.data?.items ?? [];

  const openIssue = (notification: Notification): void => {
    if (notification.readAt === null) {
      readMutation.mutate({ ids: [notification.id] });
    }
    setOpen(false);
    const projectKey = notification.issueKey.split('-')[0];
    if (projectKey) {
      navigate(`/projects/${projectKey}/board?issue=${notification.issueKey}`);
    }
  };

  return (
    <div className={styles['wrap']} ref={wrapRef}>
      <button
        type="button"
        data-testid="notifications-button"
        className={cn(styles['bell'], isOpen && styles['bellOpen'])}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={unread > 0 ? `Уведомления: ${unread} новых` : 'Уведомления'}
        onClick={() => setOpen((value) => !value)}
      >
        <Bell aria-hidden size={18} />
        {unread > 0 ? (
          <span data-testid="unread-badge" className={styles['badge']}>
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {isOpen ? (
        <div
          data-testid="notifications-panel"
          role="dialog"
          aria-label="Уведомления"
          className={styles['panel']}
        >
          <div className={styles['panelHead']}>
            <span className={styles['title']}>Уведомления</span>
            <Button
              variant="ghost"
              size="sm"
              icon={<CheckCheck size={14} />}
              disabled={unread === 0 || readMutation.isPending}
              onClick={() => readMutation.mutate({ all: true })}
            >
              Прочитать все
            </Button>
          </div>

          <div className={styles['list']}>
            {listQuery.isPending ? (
              <Skeleton variant="row" count={3} />
            ) : listQuery.isError ? (
              <EmptyState
                compact
                icon={<BellOff size={20} />}
                title="Не удалось загрузить"
                description={humanizeError(listQuery.error).message}
                action={
                  <Button variant="secondary" size="sm" onClick={() => void listQuery.refetch()}>
                    Повторить
                  </Button>
                }
              />
            ) : items.length === 0 ? (
              <EmptyState
                compact
                icon={<BellOff size={20} />}
                title="Уведомлений нет"
                description="Подпишитесь на задачу, чтобы получать изменения."
              />
            ) : (
              items.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  data-testid={`notification-${notification.id}`}
                  className={cn(
                    styles['item'],
                    notification.readAt === null && styles['unread'],
                  )}
                  onClick={() => openIssue(notification)}
                >
                  <span aria-hidden className={styles['icon']}>
                    {notificationIcon(notification.type)}
                  </span>
                  <span className={styles['content']}>
                    <span className={styles['text']}>{notification.text}</span>
                    <span className={styles['meta']}>
                      <span className={styles['key']}>{notification.issueKey}</span>
                      <span>{formatRelative(notification.createdAt)}</span>
                    </span>
                  </span>
                  <ExternalLink aria-hidden size={12} className={styles['open']} />
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
